import React from 'react'
import { FlexColumn } from '@/atoms/FlexContainers'
import { Image } from '@/atoms/Image'
import { EyebrowMD, EyebrowSM, Footer, TitleSM } from '@/atoms/Text'
import { ValidNoFeesDiscount } from '@/services/Theatrical/queries'
import { useTranslate } from '@/utils/translate/translate-client'
import { PromoImage } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext/types'

interface Props {
  code: string
  backgroundImage: PromoImage
  foregroundImage: PromoImage
  accentColor: string
  noFeesDiscount?: ValidNoFeesDiscount
  backgroundIsDark: boolean
  className?: string
}

export const DavidNoFeesBanner: React.FC<Props> = ({
  code,
  backgroundImage,
  foregroundImage,
  accentColor,
  backgroundIsDark,
  className,
}) => {
  const { t } = useTranslate('tickets')
  const textColor = backgroundIsDark ? 'core-gray-100' : 'core-gray-950'

  return (
    <div className={className}>
      <div className="relative z-[39] h-[140px] rounded-xl @container">
        <div className="absolute h-[140px] w-full overflow-hidden rounded-xl">
          <div className="absolute inset-0 z-0" />
          {/* Main background image */}
          <Image
            src={backgroundImage.url}
            className="z-10"
            fill={true}
            style={{ objectFit: 'cover', objectPosition: '50% 50%' }}
            alt="No Fees Background"
            quality={50}
          />
          {/* Main image */}
          <Image
            src={foregroundImage.url}
            className="absolute right-0 top-0 z-[11] hidden h-[140px] rounded-r-xl @xl:block"
            height={140}
            width={(foregroundImage.width / foregroundImage.height) * 140}
            alt="No Fees Foreground"
          />
        </div>
        <div className="absolute left-0 top-0 z-20 flex h-full w-full items-center justify-around gap-4 px-3 @xl:justify-start @xl:gap-6 @2xl:gap-[10%] @2xl:px-6 @4xl:justify-start">
          <FlexColumn className="flex flex-nowrap justify-center text-center">
            <TitleSM className="relative whitespace-pre-line !font-['Angel_Royal_Loudes'] !text-[2.5rem] lowercase leading-[0.7] !text-[#3C246B] @sm:!text-[2.75rem] @md:hidden">
              {t('numberOneFamilyMovieLineBreak', '#1\nFamily Movie')}
            </TitleSM>
            <TitleSM className="relative hidden !font-['Angel_Royal_Loudes'] !text-[3rem] lowercase leading-[0.7] !text-[#3C246B] @md:block @lg:!text-[3.5rem] @2xl:!text-[4rem] @5xl:!text-[5rem]">
              {t('numberOneFamilyMovie', '#1 Family Movie')}
            </TitleSM>
            <TitleSM className="relative !font-['Angel_Royal_Loudes'] !text-[2rem] lowercase leading-[0.7] !text-[#3C246B] @lg:!text-[2.5rem] @2xl:!text-[3rem] @5xl:!text-[3.5rem]">
              {t('inAmerica', 'In America')}
            </TitleSM>
            <div className="z-[70] pt-2">
              <EyebrowSM color="core-gray-800" className="block !leading-[1.2] ">
                {t('forLocationsInAmericaAsOf121925', '*For locations in America as of 12/19/25')}
              </EyebrowSM>
            </div>
          </FlexColumn>
          <FlexColumn className="flex flex-nowrap items-start justify-center">
            <FlexColumn className={'flex items-center gap-1 text-center'}>
              <EyebrowMD color={textColor} className="whitespace-nowrap" weight="semibold">
                {t('usePromoCode', 'Use Promo Code')}
              </EyebrowMD>
              <div style={{ backgroundColor: accentColor }} className="my-1 px-2 py-1">
                <TitleSM color="white" weight="bold">
                  {code}
                </TitleSM>
              </div>
              <EyebrowMD color={textColor} weight={'semibold'}>
                {t('atCheckout', 'At Checkout')}
              </EyebrowMD>
              <Footer italic color={textColor} className="block @lg:whitespace-nowrap">
                {t('availableForAngelPartneringTheaters', 'Available for Angel Partnering Theaters.')}
              </Footer>
            </FlexColumn>
          </FlexColumn>
        </div>
      </div>
    </div>
  )
}
